import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CourierManagerService } from 'src/app/services/courier-manager/courier-manager.service';
import { CourierManagerModalService } from 'src/app/services/courier-manager-modal/courier-manager-modal.service';
import { PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';

declare var KTMenu: any;

@Component({
  selector: 'app-courier-manager',
  templateUrl: './courier-manager.component.html',
  styleUrls: ['./courier-manager.component.scss']
})
export class CourierManagerComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  courierManagers: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Sorting properties
  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';

  // Filter properties
  filters = {
    search: '',
    stock_number: '',
    order_date_from: '',
    order_date_to: '',
    complete_date_from: '',
    complete_date_to: '',
    surrendered_date_from: '',
    surrendered_date_to: '',
    docs_released_date_from: '',
    docs_released_date_to: '',
    status: 'All'
  };

  constructor(
    private _courierManagerService: CourierManagerService,
    private _courierManagerModalService: CourierManagerModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadCourierManagers();
    this.setupModalSubscriptions();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  private setupModalSubscriptions(): void {
    // Subscribe to modal service changes if needed
    this.subscriptions.push(
      this._courierManagerModalService.selectedCourierManager$.subscribe(courierManager => {
        if (courierManager) {
          console.log('Selected courier manager for editing:', courierManager);
        }
      })
    );
  }

  private loadCourierManagers(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;

    // Add sorting parameters to filters
    const filtersWithSort = {
      ...filters,
      ...(this.sortBy && { sort_by: this.sortBy }),
      ...(this.sortBy && { sort_order: this.sortOrder })
    };

    this.subscriptions.push(
      this._courierManagerService.GetData(page.toString(), search, filtersWithSort).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.courierManagers = response.data.data || [];
            this.updatePaginationInfo(response.data);
          } else {
            this.courierManagers = [];
            this.resetPagination();
          }
          this.cdr.detectChanges();
          this.initializeMenus();
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading courier managers:', error);
          this.courierManagers = [];
          this.resetPagination();
          this.cdr.detectChanges();
        }
      })
    );
  }

  private updatePaginationInfo(data: any): void {
    this.paginationInfo = {
      currentPage: data.current_page || 1,
      perPage: data.per_page || 20,
      total: data.total || 0,
      lastPage: data.last_page || 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.paginationInfo.lastPage) {
      this.loadCourierManagers(page, this.searchTerm, this.filters);
    }
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    // Create a copy of filters and remove status if it's "All"
    const filtersToApply: any = { ...this.filters };
    if (filtersToApply.status === 'All') {
      delete filtersToApply.status;
    }
    this.loadCourierManagers(1, this.searchTerm, filtersToApply);
  }

  onSearch(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.filters = {
      search: '',
      stock_number: '',
      order_date_from: '',
      order_date_to: '',
      complete_date_from: '',
      complete_date_to: '',
      surrendered_date_from: '',
      surrendered_date_to: '',
      docs_released_date_from: '',
      docs_released_date_to: '',
      status: 'All'
    };
    this.loadCourierManagers(1, '', {});
  }

  private initializeMenus(): void {
    setTimeout(() => {
      KTMenu.createInstances();
    }, 100);
  }

  openAddModal(): void {
    this._courierManagerModalService.clearSelectedCourierManager();
    const modalElement = document.getElementById('kt_modal_courier_manager_add_edit');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(courierManager: any): void {
    this._courierManagerModalService.setSelectedCourierManager(courierManager);
    const modalElement = document.getElementById('kt_modal_courier_manager_add_edit');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openViewDocsReleaseModal(courierManager: any): void {
    // Set the docs_released_data in window object for the modal to access
    if (courierManager.docs_released_data) {
      (window as any).selectedDocsReleaseDataForView = courierManager.docs_released_data;

      // Small delay to ensure data is set
      setTimeout(() => {
        const modalElement = document.getElementById('kt_modal_view_docs_release_data');
        if (modalElement) {
          let modal = (window as any).bootstrap.Modal.getInstance(modalElement);
          if (!modal) {
            modal = new (window as any).bootstrap.Modal(modalElement);
          }
          modal.show();
        } else {
          console.error('View Docs Release Data modal element not found');
        }
      }, 100);
    } else {
      console.warn('No docs_released_data found for this courier manager');
    }
  }

  deleteCourierManager(courierManager: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(courierManager);
      }
    });
  }

  private performDelete(courierManager: any): void {
    const courierManagerId = courierManager._id || courierManager.id;
    this.subscriptions.push(
      this._courierManagerService.DeleteDataById(courierManagerId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Courier manager has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadCourierManagers(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete courier manager',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting courier manager:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting courier manager',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onCourierManagerAdded(): void {
    console.log('Courier manager added, refreshing list...');
    this.loadCourierManagers(this.currentPage, this.searchTerm, this.filters);
  }

  onCourierManagerUpdated(): void {
    console.log('Courier manager updated, refreshing list...');
    this.loadCourierManagers(this.currentPage, this.searchTerm, this.filters);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Sorting methods
  onSort(column: string): void {
    if (this.sortBy === column) {
      // Toggle sort order if same column
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new column and default to ascending
      this.sortBy = column;
      this.sortOrder = 'asc';
    }

    // Reload data with new sorting
    this.loadCourierManagers(1, this.searchTerm, this.filters);
  }

  getSortClass(column: string): string {
    if (this.sortBy !== column) {
      return 'table-sort-asc'; // Show ascending by default
    }
    return this.sortOrder === 'asc' ? 'table-sort-asc' : 'table-sort-desc';
  }
}
